import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LayoutDashboard, Rss, Inbox, FileEdit, Newspaper, LogOut, Home, Menu, X, Database } from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", testId: "admin-nav-dashboard", end: true },
  { to: "/admin/sources", icon: Rss, label: "Sources", testId: "admin-nav-sources" },
  { to: "/admin/feed", icon: Inbox, label: "Ingested Feed", testId: "admin-nav-feed" },
  { to: "/admin/drafts", icon: FileEdit, label: "Review Queue", testId: "admin-nav-drafts" },
  { to: "/admin/articles", icon: Newspaper, label: "Published", testId: "admin-nav-articles" },
  { to: "/admin/cache", icon: Database, label: "Cache", testId: "admin-nav-cache" },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); nav("/admin/login"); };

  const Sidebar = ({ onClick }) => (
    <>
      <div className="px-5 pt-5 pb-3">
        <Link to="/" className="flex items-center gap-2.5" data-testid="admin-logo">
          <span className="flex items-center justify-center w-8 h-8 rounded-md border border-[var(--io-gold)]/40 bg-gradient-to-br from-[var(--io-gold)]/15 to-transparent">
            <span className="font-serif text-[14px] text-[var(--io-gold)]">iO</span>
          </span>
          <div className="leading-tight">
            <div className="font-serif text-[16px] text-[var(--io-text)]">India<span className="text-[var(--io-gold)]">ODR</span></div>
            <div className="font-mono text-[9px] text-[var(--io-muted)] tracking-[0.2em] uppercase">Command Center</div>
          </div>
        </Link>
      </div>
      <nav className="px-3 space-y-1 flex-1" onClick={onClick}>
        {NAV.map(({ to, icon: Icon, label, testId, end }) => (
          <NavLink
            key={to} to={to} end={end}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] font-sans transition-colors ${
                isActive
                  ? "bg-[var(--io-gold)]/10 text-[var(--io-gold)] border border-[var(--io-gold)]/30"
                  : "text-[var(--io-text-2)] hover:text-[var(--io-text)] hover:bg-[var(--io-card-2)] border border-transparent"
              }`
            }
            data-testid={testId}
          >
            <Icon size={15} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 mt-4 border-t border-[var(--io-border)] text-[11px] font-mono">
        <div className="text-[var(--io-muted)] uppercase tracking-[0.2em] text-[9px] mb-1">Signed in</div>
        <div className="text-[var(--io-text)] break-all">{user?.email}</div>
        <div className="mt-4 space-y-1.5">
          <a href="/" className="flex items-center gap-2 text-[var(--io-text-2)] hover:text-[var(--io-gold)]" data-testid="admin-view-site"><Home size={12}/> View site</a>
          <button onClick={doLogout} className="flex items-center gap-2 text-[#EF4444] hover:text-[var(--io-text)]" data-testid="admin-logout"><LogOut size={12}/> Log out</button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--io-bg)]" data-testid="admin-layout">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 io-glass px-4 py-3 flex items-center justify-between" data-testid="admin-mobile-bar">
        <Link to="/admin" className="font-serif text-[18px] text-[var(--io-text)]">India<span className="text-[var(--io-gold)]">ODR</span></Link>
        <button onClick={() => setOpen(true)} aria-label="Menu" className="text-[var(--io-text)]" data-testid="admin-mobile-menu-open"><Menu size={20}/></button>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex flex-col fixed top-0 bottom-0 left-0 w-[240px] bg-[var(--io-bg-2)] border-r border-[var(--io-border)] z-30">
          <Sidebar />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-50 bg-[var(--io-bg)]/95 md:hidden" onClick={() => setOpen(false)}>
            <div className="w-[280px] h-full bg-[var(--io-bg-2)] border-r border-[var(--io-border)] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-end px-4 pt-4">
                <button onClick={() => setOpen(false)} className="text-[var(--io-text)]" aria-label="Close" data-testid="admin-mobile-menu-close"><X size={20}/></button>
              </div>
              <Sidebar onClick={() => setOpen(false)} />
            </div>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 md:ml-[240px] p-5 md:p-10">
          {children}
        </main>
      </div>
    </div>
  );
}
